import { CircleCheckBig, MessageSquareText, Mic, MicOff, Square } from "lucide-react";
import { OpsVoiceOrb } from "./ops-voice-orb.js";
import type { OpsVoiceController, OpsVoiceState } from "./use-ops-voice.js";

export function OpsVoiceControl({
  voice,
  state,
  busy,
  workActive,
  meetingActive
}: {
  voice: OpsVoiceController;
  state: OpsVoiceState;
  busy: boolean;
  workActive: boolean;
  meetingActive: boolean;
}) {
  // A lost voice provider can leave canonical work running in Text. Preserve
  // its explicit Stop control; changing the input mode is not completion.
  const canInterrupt = ["thinking", "speaking"].includes(state.phase);
  const canRestart = state.mode === "voice" && state.phase === "voice_arming" && state.status.startsWith("Voice paused");
  const showMicReadiness = ["arming", "ready", "error"].includes(state.micReadiness);
  // WHY [BOL-TEXT-WORK-READINESS-001]: pending Text retains Voice's idle
  // readiness string even while input is disabled. Chat already owns truthful
  // progress; omit only this idle Text helper, not Voice/error status or its
  // mounted accessibility target. Falsifier: pending Text says ready, or idle,
  // microphone, and Voice statuses disappear with it.
  const hideTextIdleStatus = state.mode === "text" && state.phase === "text_idle" && workActive;
  // WHY [BOL-ORB-SHARED-WORK-STATE-001]: transport can legitimately say
  // Listening while Text work, Manual apply, or a resumed realtime turn is
  // still unresolved. The inline row and main Orb must share that application
  // work owner. Actual PCM remains stronger so speech never looks like scan.
  // Falsifier: an unresolved row scans while the main Orb idles, or work state
  // overwrites a real speaking phase.
  const orbPhase = state.phase === "speaking" ? "speaking" : workActive ? "thinking" : state.phase;
  return (
    <section
      className="ops-voice-control"
      aria-label="Luna input and voice"
      data-voice-phase={state.phase}
      data-mic-readiness={state.micReadiness}
      data-voice-turn-id={state.turnId ?? undefined}
      data-answer-latency-ms={state.answerLatencyMs ?? undefined}
      data-first-audio-latency-ms={state.firstAudioLatencyMs ?? undefined}
      data-first-interim-latency-ms={state.firstInterimLatencyMs ?? undefined}
      data-finalization-latency-ms={state.finalizationLatencyMs ?? undefined}
      data-transport-elapsed-ms={state.transportElapsedMs ?? undefined}
      data-microphone-state={state.muted ? "muted" : "live"}
    >
      <OpsVoiceOrb phase={orbPhase} pulse={state.pulse} pulseIntensity={state.pulseIntensity} />
      <div className="ops-voice-modes" role="group" aria-label="Ops Agent input mode">
        <button
          type="button"
          className={state.mode === "text" ? "is-active" : ""}
          aria-pressed={state.mode === "text"}
          onClick={voice.selectText}
        ><MessageSquareText size={15} aria-hidden="true" />Text</button>
        <button
          type="button"
          className={state.mode === "voice" ? "is-active" : ""}
          aria-pressed={state.mode === "voice"}
          aria-busy={state.micReadiness === "arming"}
          aria-describedby="ops-voice-status"
          disabled={(busy && state.mode !== "voice") || meetingActive}
          onClick={voice.activateVoice}
        >
          <span className="ops-voice-mode__icon" aria-hidden="true">
            <Mic size={15} />
            {showMicReadiness ? (
              <span className="ops-voice-mic-readiness" data-state={state.micReadiness} />
            ) : null}
          </span>
          Voice
        </button>
      </div>
      {/* WHY [BOL-VOICE-COMPOSER-STATUS-ONLY-001]: partial ASR is mirrored as
          one transient Chat draft; input-final/revisions own the Chat turn.
          Repeating state.transcript here made one utterance appear twice and
          could repeat Luna's acknowledgment beside the same Chat exchange.
          The composer owns only short lifecycle/control status; Chat retains
          the complete streaming/final transcript. Falsifier: a Voice utterance
          appears below Chat, or useful readiness/error/Stop status disappears. */}
      <p id="ops-voice-status" className={state.error ? "ops-voice-status is-error" : "ops-voice-status"} role="status">
        {hideTextIdleStatus ? null : <span>{state.status}</span>}
      </p>
      {state.approvalConsentPending ? (
        <button className="ops-voice-interrupt ops-voice-consent" type="button" onClick={voice.answerSpokenApproval}>
          <CircleCheckBig size={15} aria-hidden="true" />Answer approval
        </button>
      ) : canInterrupt ? (
        <button className="ops-voice-interrupt" type="button" onClick={voice.stopAndListen}>
          <Square size={13} fill="currentColor" aria-hidden="true" />{state.mode === "voice" ? "Stop / Listen" : "Stop"}
        </button>
      ) : canRestart ? (
        <button className="ops-voice-interrupt" type="button" onClick={voice.activateVoice}>
          <Mic size={14} aria-hidden="true" />Listen
        </button>
      ) : null}
      {state.mode === "voice" ? (
        <button
          className={state.muted ? "ops-voice-mute is-muted" : "ops-voice-mute"}
          type="button"
          aria-label={state.muted ? "Unmute microphone" : "Mute microphone"}
          aria-pressed={state.muted}
          onClick={voice.toggleMute}
        >
          {state.muted
            ? <MicOff size={17} aria-hidden="true" />
            : <Mic size={17} aria-hidden="true" />}
          <span>{state.muted ? "Muted" : "Mute"}</span>
        </button>
      ) : null}
    </section>
  );
}
